import './Toolkit/setPrimeReactLang'

import React from 'react'

import { useRef, useState, useEffect, useCallback, useMemo, memo } from 'react'

import { v4 as uniqid } from 'uuid'

import { Calendar } from 'primereact/calendar'

import { PrimeReactProvider } from 'primereact/api'

import { HiOutlineExclamation } from 'react-icons/hi'
import { LuCalendarDays }       from 'react-icons/lu'

import { DateTime } from 'luxon'

import DateVisualHelper from './Toolkit/DateVisualHelper'
import InputMask        from './Toolkit/InputMask'
import MASK_CONFIGS     from './Toolkit/DateFunctions/mask_configs'

import Loader from '@Pure/Loader'

import nodeIsOrBelongsTo from '@function/nodeIsOrBelongsTo'
import arePropsEqual     from '@function/arePropsEqual'
import bemit             from '@function/bemit'

import FormsStore from '@Store/Forms'

const prime_params = { unstyled: true };

export default memo(function DatePicker ({ removeError, name, onChange, ...props })
{
	const default_value = props['default-value'] || '';

	const onEnter = props.onEnter;

	const type = typeof onChange === 'function'
	? 'legacy'
	: 'pullstate';

	const MASK_CONFIG = useMemo(function ()
	{
		const MASK_CONFIG_NAME = Object.keys(MASK_CONFIGS)
		.find(name => MASK_CONFIGS[name].format === props.mask);

		return MASK_CONFIGS[MASK_CONFIG_NAME] || MASK_CONFIGS.french;
	},
	[props.mask]);

	const FORM_NAME = props.form || 'no-form';

	const is__disabled = props['read-only'];

	const state_value = FormsStore.useState(s => s[FORM_NAME]?.[name]);

	const container_ref = useRef();
	const input_ref = useRef();
	const input_value_ref = useRef();
	const background_ref = useRef();

	const [id, setId] = useState(uniqid());

	const [is__visible, setVisibility] = useState(false);
	const [mask_text, setMaskText] = useState('');
	const [user_value, setUserValue] = useState('');
	const [calendar_date, setCalendarDate] = useState('');

	const is__inline = props.inline === true;

	const modifiers = useMemo(function ()
	{
		const modifiers = (props.bemit || []).concat(props.error ? ['error'] : []);

		if (is__disabled) modifiers.push('disabled');

		if (MASK_CONFIG.format === 'YYYY') modifiers.push('year');

		if (props.type === 'week') modifiers.push('week');

		if (is__inline) modifiers.push('inline');

		return modifiers;
	},
	[props.bemit, props.error, is__disabled, MASK_CONFIG, props.type, is__inline]);

	const label_class = useMemo(function ()
	{
		const modifiers = ['date-picker'].concat(props.error ? ['error'] : []);
		if (is__disabled) modifiers.push('disabled');
		if (is__inline) modifiers.push('inline');

		return bemit('c-form-element-label', modifiers);
	},
	[props.error, is__disabled, is__inline]);

	useEffect(function ()
	{
		if (MASK_CONFIG) setMaskText(MASK_CONFIG.format);
	},
	[MASK_CONFIG]);

// initialisation avec valeur par défaut
	useEffect(function ()
	{
		if (!input_ref?.current || !MASK_CONFIG) return;

		if (type === 'pullstate' && (state_value !== undefined || !FORM_NAME)) return;

		if (type === 'legacy' && props.value !== undefined) return;

		(function init ()
		{
			input_ref.current.value = default_value
			? MASK_CONFIG.humanValueFromIso(default_value)
			: '';

			input_value_ref.current = input_ref.current.value;

			setUserValue(input_ref.current.value);

			if (default_value)
			{
				const date = DateTime.fromISO(default_value);

				if (date.isValid)
				{
					setCalendarDate(date.toJSDate());
				}
			}

			if (type === 'legacy')
			{
				onChange({ e: null, name, value: default_value || '' });

				return;
			}

			FormsStore.update(function (state)
			{
				state[FORM_NAME] = state[FORM_NAME] || { };

				state[FORM_NAME][name] = default_value
				? default_value
				: '';
			});
		})();
	},
	[default_value, FORM_NAME, name, MASK_CONFIG, state_value, type, onChange, props.value]);

// masque le calendrier quand on click en dehors
	useEffect(() =>
	{
		if (is__disabled || !is__visible)
		{
			setVisibility(false);
			return;
		}

		document.addEventListener('click', listener);

		return () => document.removeEventListener('click', listener);

		function listener (e)
		{
		// ignore le click s'il est dans le date picker
			if (nodeIsOrBelongsTo(e?.target, container_ref.current)) return;

		// la navigation dans le picker change le layout et le parent n'existe plus,
		// ça pourraît se produire en cliquant ailleurs mais j'estime le risque
		// très limité
			if (!e?.target?.parentElement) return;

			const target = e?.target;

		// même pb que ci-dessus mais pour un des ancêtres, ce n'est pas idéal mais
		// on vérifie la présence de l'attribut html data-pc-section
		// au pire ce qui peut arriver c'est que s'il y a deux calendriers d'ouvert,
		// rien ne se passe, c'est très acceptable à mon sens
			const is__in_calendar = (target?.getAttributeNames?.() || [])
			.some(attr => attr.startsWith('data-pc-section'));

			if (is__in_calendar) return;

			setVisibility(false);
		}
	},
	[is__visible, is__disabled, container_ref]);

	const handleDatePickedChange = useCallback(function (e)
	{
		if (!MASK_CONFIG) return;

		if (props.error && removeError) removeError(name);

		const js_date = e.value;

		if (js_date)
		{
			setCalendarDate(js_date);
		}

		const new_ISO_date = js_date
		? DateTime.fromJSDate(js_date).toFormat(MASK_CONFIG.iso_format)
		: '';

		const human_value = new_ISO_date
		? MASK_CONFIG.humanValueFromIso(new_ISO_date)
		: '';

		input_value_ref.current = human_value; // stocke la valeur de l'input

		if (input_ref.current) // met à jour le texte de l'input
		{
			input_ref.current.value = input_value_ref.current;
		}

		setUserValue(human_value);
		setVisibility(false);

		if (type === 'legacy')
		{
			onChange({ e, name, value: new_ISO_date });

			return;
		}

		FormsStore.update(function (state)
		{
			state[FORM_NAME] = state[FORM_NAME] || { };

			state[FORM_NAME][name] = new_ISO_date;
		});
	},
	[props.error, removeError, name, FORM_NAME, MASK_CONFIG, type, onChange]);

// changement du pullstate ou de props.value
	useEffect(function ()
	{
		const changed_value = type === 'pullstate'
		? state_value
		: props.value;

		if (changed_value === undefined || !input_ref?.current) return;

		if (!changed_value && input_ref.current.value)
		{
			input_ref.current.value = '';
			setUserValue('');
		}
		else if (changed_value)
		{
			const numbers_only = Array.from(changed_value || '')
			.filter(char => numbers_as_str.includes(char))
			.join('');

		// si la valeur du state n'est pas exploitable on l'ignore
			if (numbers_only.length !== MASK_CONFIG.luxon_format.length) return;

			let as_date = DateTime
			.fromFormat(changed_value, MASK_CONFIG.iso_format);

			if (!as_date.isValid)
			{
				as_date = DateTime
				.fromFormat(changed_value, MASK_CONFIG.luxon_format);
			}

			if (as_date.isValid)
			{
				input_ref.current.value = MASK_CONFIG
				.humanValueFromIso(as_date.toFormat(MASK_CONFIG.iso_format));

				input_value_ref.current = input_ref.current.value;
				setUserValue(input_ref.current.value);

				setCalendarDate(as_date.toJSDate());
			}
		}
	},
	[state_value, props.value, MASK_CONFIG, type]);

	const handleInputChange = useCallback(function (e)
	{
		const last_char = e.target.value[e.target.value.length-1] || '';

	// si on ajoute un car. à la fin de l'input
		if (e.target.value === ((input_value_ref.current || '') + last_char))
		{
			if (typeof MASK_CONFIG.temperUserInput === 'function')
			{
				MASK_CONFIG.temperUserInput(e.target, MASK_CONFIG.format);
			}
		}

		const value = e.target.value;

	// masque le calendrier pour laisser place à l'aide à la saisie
		if (value !== input_value_ref.current)
		{
			setVisibility(false);
		}

		input_value_ref.current = value;
		setUserValue(value);

		const numbers_only = Array.from(value || '')
		.filter(char => numbers_as_str.includes(char))
		.join('');

		const luxon_format = MASK_CONFIG.luxon_format;
		const format_length = luxon_format.length;

		let new_value = numbers_only;

		if (numbers_only.length === format_length)
		{
			const as_date = DateTime.fromFormat(numbers_only, luxon_format);

			if (as_date.isValid)
			{
				new_value = as_date.toFormat(MASK_CONFIG.iso_format);

				setCalendarDate(as_date.toJSDate());
			}
		}

		if (type === 'legacy')
		{
			onChange({ e, name, value: new_value });

			return;
		}

		FormsStore.update(function (state)
		{
			state[FORM_NAME] ||= { };

			state[FORM_NAME][name] = new_value;
		});
	},
	[MASK_CONFIG, FORM_NAME, name, onChange, type]);

	const handleKeyDown = useCallback(function (e)
	{
		if (e.code === 'Escape') setVisibility(false);

		if (e.code === 'Enter' && typeof onEnter === 'function') onEnter(e);
	},
	[onEnter]);

	const handleBackgroundClick = useCallback(function (e)
	{
		if (e?.target === background_ref?.current) setVisibility(false);
	},
	[background_ref]);

	const characters_count = user_value.length;

	return (
		<div className={ bemit('primeract-calendar', modifiers) }
			ref={ container_ref }>

			{
				props.label ?
				(
					<label htmlFor={ props.id || id }
						className={ label_class }>
						{ props.label }
					</label>
				)
				: undefined
			}

			<div className="primeract-calendar__wrapper">
				<InputMask value={ mask_text }
					className="primeract-calendar__mask"
					hide={ characters_count }
				/>

				<input type="text" className="primeract-calendar__input"
					onChange={ handleInputChange }
					onKeyDown={ handleKeyDown }
					ref={ input_ref }
					id={ id }
					disabled={ is__disabled }
				/>

				{
					props['with-picker'] !== false ?
					(
						<button type="button" className="primeract-calendar__toggle"
							aria-labelledby={ id + '-toggle' }
							onClick={ e => setVisibility(true) }
							onKeyDown={ handleKeyDown }
							disabled={ is__disabled }>
							<span className="u-readers-only" id={ id + '-toggle' }>
								Utiliser le calendrier
							</span>
							<LuCalendarDays className="primeract-calendar__toggle-icon" />
						</button>
					)
					: undefined
				}

				<DateVisualHelper mask={ mask_text }
					user_value={ user_value || '' }
					className="primeract-calendar__visual-helper"
					incomplete={
						characters_count > 0 &&
						characters_count < MASK_CONFIG.format?.length
					}
					invalid={
						characters_count > 0 &&
						characters_count > MASK_CONFIG.format?.length
					}
				/>

				{
					props['with-picker'] !== false && is__visible ?
					(
						<div className="primeract-calendar__calendar-background"
							onClick={ handleBackgroundClick }
							onKeyDown={ handleKeyDown }
							ref={ background_ref }
						/>
					)
					: undefined
				}

				{
					props['with-picker'] !== false ?
					(
						<PrimeReactProvider value={ prime_params }>
							<Calendar inline showIcon={ false }
								locale="fr"
								onChange={ handleDatePickedChange }
								className={
									'primeract-calendar__calendar ' +
									(is__visible ? ' visible' : 'hidden')
								}
								appendTo="self"
								disabled={ is__disabled }
								view={
									MASK_CONFIG.format === 'YYYY'
									? 'year' : 'date'
								}
								showWeek={ props['display-week'] === true }
								value={ calendar_date }
							/>
						</PrimeReactProvider>
					)
					: undefined
				}
			</div>

			{
				props.error ?
				(
					<span className={ bemit('c-field-error', ['input']) }>
						<HiOutlineExclamation className="c-field-error__icon" />
						{ props.error }
					</span>
				)
				: undefined
			}

			{
				props.loading === true ?
				(
					<Loader
						className="c-loader" bemit={ ['primary', 'bg-semi-white'] }
					/>
				)
				: undefined
			}

		</div>
	);
},
arePropsEqual);

const numbers_as_str = Array.from({ length: 10 }).map((_v, i) => String(i));